import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, BellRing, Mail, Pencil, Plus, Send } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime } from '../lib/format';
import {
  alertKindTone,
  deliveryTone,
  severityLabel,
  severityTone,
  type AlertDeliveryRow,
  type AlertRecipient,
  type MonitoringCoverage,
  type MonitoringSettingsInfo,
  type TelegramStatus,
} from '../lib/monitoring';
import { MonitorBanner } from '../components/MonitorBanner';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingBlock,
  PageHeader,
  Pagination,
  Select,
  Table,
  Td,
  Th,
  Tr,
  useToast,
} from '../components/ui';

/**
 * Monitoring › Alerts (operator's spec, 2026-10-20): who is alerted — email
 * addresses and Telegram chats, each from an importance up and about the servers
 * or projects picked — whether the channels are set up on this server, and the
 * log of every alert sent. The monitor sends them; a test goes from here.
 */
export function MonitoringAlertsPage() {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const recipients = useQuery({
    queryKey: ['monitoring', 'recipients'],
    queryFn: () => api.get<AlertRecipient[]>('/monitoring/recipients'),
  });
  const settings = useQuery({
    queryKey: ['monitoring', 'settings'],
    queryFn: () => api.get<MonitoringSettingsInfo>('/monitoring/settings'),
  });
  const coverage = useQuery({
    queryKey: ['monitoring', 'coverage'],
    queryFn: () => api.get<MonitoringCoverage>('/monitoring/coverage'),
  });
  const telegram = useQuery({
    queryKey: ['monitoring', 'telegram'],
    queryFn: () => api.get<TelegramStatus>('/monitoring/telegram'),
    staleTime: 60_000,
  });
  const deliveries = useQuery({
    queryKey: ['monitoring', 'deliveries', page, filters],
    queryFn: () => api.get<Paginated<AlertDeliveryRow>>('/monitoring/deliveries', { page, pageSize: 25, ...filters }),
    refetchInterval: 15_000,
  });

  const test = useMutation({
    mutationFn: (id: string) => api.post<{ status: string; error: string | null }>(`/monitoring/recipients/${id}/test`),
    onSuccess: (result) => {
      if (result.status === 'SENT') toast.success(t('monitoring.alerts.testSent'));
      else toast.error(`${t(`monitoring.delivery.${result.status}` as TranslationKey)}: ${result.error ?? ''}`);
      void deliveries.refetch();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const alertsOff = settings.data?.values['monitoring.alertsEnabled'] === false;

  return (
    <>
      <PageHeader
        title={t('monitoring.alerts.title')}
        subtitle={t('monitoring.alerts.subtitle')}
        action={
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/monitoring/alerts/new')}>
            {t('monitoring.alerts.new')}
          </Button>
        }
      />
      <MonitorBanner />

      {alertsOff && (
        <p className="mb-5 rounded-md border border-warning/40 bg-warning-bg px-4 py-3 text-body-sm text-warning-fg dark:bg-warning/10 dark:text-warning-dark">
          {t('monitoring.alerts.off')}
        </p>
      )}

      {!alertsOff && coverage.data && coverage.data.uncovered.length > 0 && <CoverageWarning coverage={coverage.data} />}

      <div className="mb-5 grid gap-4 md:grid-cols-2">
        <Card>
          <div className="flex items-start gap-3">
            <Mail className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
            <div className="min-w-0">
              <p className="text-title text-[var(--text-primary)]">{t('monitoring.channel.EMAIL')}</p>
              {settings.data?.email.configured ? (
                <p className="text-body-sm text-success-fg dark:text-success">{t('monitoring.alerts.emailReady')}</p>
              ) : (
                <p className="text-body-sm text-warning-fg dark:text-warning-dark">
                  {t('monitoring.alerts.emailMissing', { missing: (settings.data?.email.missing ?? []).join(', ') })}
                </p>
              )}
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-start gap-3">
            <Send className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
            <div className="min-w-0">
              <p className="text-title text-[var(--text-primary)]">{t('monitoring.channel.TELEGRAM')}</p>
              {!telegram.data ? (
                <p className="text-body-sm text-muted">{t('common.loading')}</p>
              ) : !telegram.data.configured ? (
                <p className="text-body-sm text-warning-fg dark:text-warning-dark">{t('monitoring.alerts.telegramMissing')}</p>
              ) : telegram.data.bot ? (
                <p className="text-body-sm text-success-fg dark:text-success">
                  {t('monitoring.alerts.telegramReady', { bot: telegram.data.bot.username ? `@${telegram.data.bot.username}` : telegram.data.bot.name ?? '' })}
                </p>
              ) : (
                <p className="text-body-sm text-error-fg dark:text-error-dark">{telegram.data.error}</p>
              )}
            </div>
          </div>
        </Card>
      </div>

      <Card padded={false} className="mb-5">
        <div className="p-5 pb-0">
          <CardHeader title={t('monitoring.alerts.recipients')} subtitle={t('monitoring.alerts.recipientsHint')} />
        </div>
        {recipients.isLoading ? (
          <LoadingBlock />
        ) : recipients.error ? (
          <ErrorState error={recipients.error} onRetry={() => void recipients.refetch()} />
        ) : !recipients.data?.length ? (
          <EmptyState
            icon={<BellRing className="h-8 w-8" />}
            title={t('monitoring.alerts.empty')}
            hint={t('monitoring.alerts.emptyHint')}
            action={
              <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/monitoring/alerts/new')}>
                {t('monitoring.alerts.new')}
              </Button>
            }
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('common.name')}</Th>
                <Th>{t('monitoring.alerts.sendTo')}</Th>
                <Th>{t('monitoring.alerts.from')}</Th>
                <Th>{t('monitoring.alerts.covers')}</Th>
                <Th>{t('common.status')}</Th>
                <Th align="end">{t('common.actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {recipients.data.map((r) => {
                const scoped = r.allProjects || (r.servers?.length ?? 0) + (r.projects?.length ?? 0) > 0;
                return (
                  <Tr key={r.id}>
                    <Td>
                      <div className="font-medium">{r.name}</div>
                      <div className="text-caption text-muted">
                        {r.locale === 'ar' ? 'العربية' : 'English'}
                        {!r.notifyRecovery && ` · ${t('monitoring.alerts.noRecovery')}`}
                      </div>
                    </Td>
                    <Td>
                      <div className="text-caption text-muted">{t(`monitoring.channel.${r.channel}` as TranslationKey)}</div>
                      <span dir="ltr" className="font-mono text-mono-sm">{r.target}</span>
                    </Td>
                    <Td>
                      <Badge>{t(severityLabel(r.minSeverity))}</Badge>
                    </Td>
                    <Td>
                      {scoped ? (
                        <div className="flex flex-wrap gap-1.5">
                          {r.servers?.map((s) => <Badge key={s.id}>{s.name}</Badge>)}
                          {r.allProjects ? (
                            <Badge tone="brand">{t('monitoring.alerts.allProjects')}</Badge>
                          ) : (
                            r.projects?.map((p) => (
                              <Badge key={p.id} tone="brand" title={localized(p)}>{p.code}</Badge>
                            ))
                          )}
                        </div>
                      ) : (
                        <span className="text-body-sm text-muted">{t('monitoring.alerts.everything')}</span>
                      )}
                    </Td>
                    <Td>
                      <Badge tone={r.isActive ? 'success' : 'neutral'} dot>
                        {r.isActive ? t('monitoring.alerts.active') : t('monitoring.alerts.inactive')}
                      </Badge>
                      {r.lastAlertAt && <div className="mt-0.5 text-caption text-faint">{formatDateTime(r.lastAlertAt, locale)}</div>}
                    </Td>
                    <Td align="end">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          icon={<Send className="h-4 w-4" />}
                          loading={test.isPending && test.variables === r.id}
                          onClick={() => test.mutate(r.id)}
                        >
                          {t('monitoring.alerts.test')}
                        </Button>
                        <Button size="sm" variant="ghost" icon={<Pencil className="h-4 w-4" />} onClick={() => navigate(`/monitoring/alerts/${r.id}/edit`)}>
                          {t('common.edit')}
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      <Card padded={false}>
        <div className="flex flex-wrap items-start justify-between gap-3 p-5 pb-3">
          <CardHeader title={t('monitoring.alerts.log')} subtitle={t('monitoring.alerts.logHint')} className="mb-0" />
          <div className="flex gap-2">
            <Select
              value={filters.status ?? ''}
              onChange={(e) => {
                setFilters({ ...filters, status: e.target.value });
                setPage(1);
              }}
            >
              <option value="">{t('monitoring.filter.allStatuses')}</option>
              {(['SENT', 'QUEUED', 'FAILED', 'SKIPPED'] as const).map((s) => (
                <option key={s} value={s}>{t(`monitoring.delivery.${s}` as TranslationKey)}</option>
              ))}
            </Select>
            <Select
              value={filters.kind ?? ''}
              onChange={(e) => {
                setFilters({ ...filters, kind: e.target.value });
                setPage(1);
              }}
            >
              <option value="">{t('monitoring.alerts.allKinds')}</option>
              {(['DOWN', 'RECOVERED', 'REMINDER', 'SSL_EXPIRING', 'MONITOR_OFFLINE', 'TEST'] as const).map((k) => (
                <option key={k} value={k}>{t(`monitoring.alertKind.${k}` as TranslationKey)}</option>
              ))}
            </Select>
          </div>
        </div>
        {deliveries.isLoading ? (
          <LoadingBlock />
        ) : deliveries.error ? (
          <ErrorState error={deliveries.error} onRetry={() => void deliveries.refetch()} />
        ) : !deliveries.data?.items.length ? (
          <p className="px-5 pb-5 text-body-sm text-muted">{t('monitoring.alerts.logEmpty')}</p>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('monitoring.alerts.when')}</Th>
                  <Th>{t('monitoring.alerts.recipient')}</Th>
                  <Th>{t('monitoring.alerts.what')}</Th>
                  <Th>{t('common.status')}</Th>
                </tr>
              </thead>
              <tbody>
                {deliveries.data.items.map((d) => (
                  <Tr key={d.id}>
                    <Td className="whitespace-nowrap tabular text-muted">{formatDateTime(d.createdAt, locale)}</Td>
                    <Td>
                      <div>{d.recipient?.name ?? '—'}</div>
                      <div className="text-caption text-muted">
                        {t(`monitoring.channel.${d.channel}` as TranslationKey)} ·{' '}
                        <span dir="ltr" className="font-mono">{d.target}</span>
                      </div>
                    </Td>
                    <Td>
                      <Badge tone={alertKindTone(d.kind)}>{t(`monitoring.alertKind.${d.kind}` as TranslationKey)}</Badge>
                      {/* An Arabic recipient's subject, laid out as Arabic whatever the page's language. */}
                      <div dir="auto" className="mt-1 text-body-sm">{d.subject}</div>
                    </Td>
                    <Td>
                      <Badge tone={deliveryTone(d.status)} dot>{t(`monitoring.delivery.${d.status}` as TranslationKey)}</Badge>
                      {d.lastError && <div className="mt-1 max-w-xs text-caption text-error-fg dark:text-error-dark">{d.lastError}</div>}
                      {d.attempts > 1 && <div className="text-caption text-faint">{t('monitoring.alerts.attempts', { n: d.attempts })}</div>}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={deliveries.data.page} pageCount={deliveries.data.pageCount} total={deliveries.data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}

/** The most URLs named under each reason before "+N more". */
const COVERAGE_SHOWN = 8;

/**
 * The watched URLs whose outage reaches nobody, by why (operator, 2026-10-21:
 * the Admin URL went down and nothing came — it was Medium, and both recipients
 * asked for High). Each links to its URL's page, where its importance is set.
 */
function CoverageWarning({ coverage }: { coverage: MonitoringCoverage }) {
  const { t } = useI18n();
  const gaps = (['NO_RECIPIENTS', 'IMPORTANCE', 'SCOPE'] as const)
    .map((gap) => ({ gap, urls: coverage.uncovered.filter((u) => u.gap === gap) }))
    .filter((g) => g.urls.length > 0);
  return (
    <div
      role="status"
      className="mb-5 flex items-start gap-3 rounded-md border border-warning/40 bg-warning-bg px-4 py-3 text-body-sm text-warning-fg dark:bg-warning/10 dark:text-warning-dark"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0 space-y-2">
        <p className="font-medium">{t('monitoring.coverage.title', { count: coverage.uncovered.length, total: coverage.watched })}</p>
        {gaps.map(({ gap, urls }) => (
          <div key={gap}>
            <p>{t(`monitoring.coverage.${gap}` as TranslationKey)}</p>
            <ul className="mt-1.5 flex flex-wrap gap-1.5">
              {urls.slice(0, COVERAGE_SHOWN).map((u) => (
                <li key={u.id}>
                  <Link
                    to={`/monitoring/urls/${u.id}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-warning/40 bg-[var(--surface-raised)] px-2 py-1 text-[var(--text-primary)] hover:border-warning"
                    title={u.serverName}
                  >
                    <span className="font-medium">{u.name}</span>
                    <Badge tone={severityTone(u.severity)}>{t(severityLabel(u.severity))}</Badge>
                  </Link>
                </li>
              ))}
              {urls.length > COVERAGE_SHOWN && (
                <li className="self-center">{t('monitoring.coverage.more', { count: urls.length - COVERAGE_SHOWN })}</li>
              )}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
